import { deepEqual } from "./deepEqual";
import type { NewId } from "./ids";
import { normalize, normalizeRatios } from "./normalize";
import {
  activePane,
  allPanes,
  findNode,
  findPane,
  firstGroup,
  idGen,
  lastGroup,
  makePane,
  parentOf,
} from "./tree";
import type {
  Edge,
  IdOptions,
  LayoutNode,
  LayoutTree,
  Pane,
  PaneInit,
  Position,
  SplitDirection,
  TabGroup,
} from "./types";

// ---------------------------------------------------------------------------
// Internal helpers (structure-preserving; callers normalize at the end)
// ---------------------------------------------------------------------------

/** Post-order map so a replacement that wraps a node is not itself revisited. */
function mapNode(node: LayoutNode, fn: (node: LayoutNode) => LayoutNode): LayoutNode {
  const next: LayoutNode =
    node.type === "split"
      ? { ...node, children: node.children.map((child) => mapNode(child, fn)) }
      : node;
  return fn(next);
}

function mapTree(tree: LayoutTree, fn: (node: LayoutNode) => LayoutNode): LayoutTree {
  return { ...tree, root: mapNode(tree.root, fn) };
}

function updateGroup(
  tree: LayoutTree,
  groupId: string,
  fn: (group: TabGroup) => TabGroup,
): LayoutTree {
  return mapTree(tree, (node) => (node.type === "tabs" && node.id === groupId ? fn(node) : node));
}

function updatePane(tree: LayoutTree, paneId: string, fn: (pane: Pane) => Pane): LayoutTree {
  return mapTree(tree, (node) =>
    node.type === "tabs" && node.tabs.some((tab) => tab.id === paneId)
      ? { ...node, tabs: node.tabs.map((tab) => (tab.id === paneId ? fn(tab) : tab)) }
      : node,
  );
}

function withActive(group: TabGroup, activeTabId: string | undefined): TabGroup {
  const out: TabGroup = { type: "tabs", id: group.id, tabs: group.tabs };
  if (activeTabId !== undefined) out.activeTabId = activeTabId;
  return out;
}

/** Removes a tab; when it was the active tab, the tab at the same index (or the last) takes over. */
function removeTab(group: TabGroup, paneId: string): TabGroup {
  const index = group.tabs.findIndex((tab) => tab.id === paneId);
  if (index === -1) return group;
  const tabs = group.tabs.filter((tab) => tab.id !== paneId);
  const active =
    group.activeTabId === paneId ? tabs[Math.min(index, tabs.length - 1)]?.id : group.activeTabId;
  return withActive({ ...group, tabs }, active);
}

function removePaneRaw(tree: LayoutTree, paneId: string): LayoutTree {
  return mapTree(tree, (node) =>
    node.type === "tabs" && node.tabs.some((tab) => tab.id === paneId)
      ? removeTab(node, paneId)
      : node,
  );
}

/** An emptied sibling returns its space to the destination, rather than resizing every sibling. */
function removeForMove(tree: LayoutTree, paneId: string, targetGroupId: string): LayoutTree {
  const found = findPane(tree, paneId);
  const removed = removePaneRaw(tree, paneId);
  if (!found || found.group.tabs.length !== 1 || found.group.id === targetGroupId) return removed;
  const parent = parentOf(tree, found.group.id);
  if (!parent) return removed;
  const from = parent.children.findIndex((child) => child.id === found.group.id);
  const to = parent.children.findIndex((child) =>
    findNode({ ...tree, root: child }, targetGroupId),
  );
  if (to < 0 || to === from) return removed;
  return mapTree(removed, (node) => {
    if (node.type !== "split" || node.id !== parent.id) return node;
    const ratios = [...node.ratios];
    ratios[to] = (ratios[to] ?? 0) + (ratios[from] ?? 0);
    // Remove the empty child now so normalization never sees a zero ratio.
    return {
      ...node,
      children: node.children.filter((_, i) => i !== from),
      ratios: ratios.filter((_, i) => i !== from),
    };
  });
}

/**
 * Places `newNode` beside the group `groupId`. When the group's parent split has the same
 * direction the node becomes a sibling taking half of the group's ratio; otherwise the group is
 * wrapped in a new 50/50 split.
 */
function insertBeside(
  tree: LayoutTree,
  groupId: string,
  newNode: LayoutNode,
  direction: SplitDirection,
  position: Position,
  gen: NewId,
): LayoutTree {
  const parent = parentOf(tree, groupId);
  if (parent && parent.direction === direction) {
    return mapTree(tree, (node) => {
      if (node.type !== "split" || node.id !== parent.id) return node;
      const index = node.children.findIndex((child) => child.id === groupId);
      const ratios = normalizeRatios(node.ratios, node.children.length);
      const share = (ratios[index] ?? 0) / 2;
      const children = [...node.children];
      const nextRatios = [...ratios];
      nextRatios[index] = share;
      const at = position === "before" ? index : index + 1;
      children.splice(at, 0, newNode);
      nextRatios.splice(at, 0, share);
      return { ...node, children, ratios: nextRatios };
    });
  }
  return mapTree(tree, (node) =>
    node.id === groupId
      ? {
          type: "split",
          id: gen(),
          direction,
          children: position === "before" ? [newNode, node] : [node, newNode],
          ratios: [0.5, 0.5],
        }
      : node,
  );
}

/** The active pane of the closest sibling group (previous first, then next) in the parent split. */
function siblingPane(tree: LayoutTree, groupId: string): string | undefined {
  const parent = parentOf(tree, groupId);
  if (!parent) return undefined;
  const index = parent.children.findIndex((child) => child.id === groupId);
  const previous = parent.children[index - 1];
  const next = parent.children[index + 1];
  const group = previous ? lastGroup(previous) : next ? firstGroup(next) : undefined;
  return group ? activePane(group)?.id : undefined;
}

function defaultGroup(tree: LayoutTree): TabGroup | undefined {
  const focused = tree.focusedPaneId !== undefined ? findPane(tree, tree.focusedPaneId) : undefined;
  return focused?.group ?? firstGroup(tree.root);
}

// ---------------------------------------------------------------------------
// Public operations. All are pure: they return a new, normalized tree and return the input
// tree unchanged when an id cannot be found.
// ---------------------------------------------------------------------------

export type OpenPaneOptions = IdOptions & {
  /** Group to add the tab to. Default: the focused pane's group, else the first group. */
  groupId?: string;
  /** Make the new tab active and focused (default true). */
  activate?: boolean;
  /** Reuse an existing pane with the same `paneType` and deep-equal `params` (default true). */
  dedupe?: boolean;
};

export function openPane(tree: LayoutTree, pane: PaneInit, opts: OpenPaneOptions = {}): LayoutTree {
  const activate = opts.activate ?? true;
  if (opts.dedupe ?? true) {
    const params = pane.params ?? {};
    const existing = allPanes(tree).find(
      (p) => p.paneType === pane.paneType && deepEqual(p.params, params),
    );
    if (existing) return activate ? activateTab(tree, existing.id) : tree;
  }
  const target = opts.groupId !== undefined ? findNode(tree, opts.groupId) : defaultGroup(tree);
  if (!target || target.type !== "tabs") return tree;
  const newPane = makePane(pane, idGen(opts));
  const next = updateGroup(tree, target.id, (group) =>
    withActive(
      { ...group, tabs: [...group.tabs, newPane] },
      activate ? newPane.id : (group.activeTabId ?? newPane.id),
    ),
  );
  return normalize(activate ? { ...next, focusedPaneId: newPane.id } : next, opts);
}

export function closePane(tree: LayoutTree, paneId: string, opts: IdOptions = {}): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  const next = updateGroup(tree, found.group.id, (group) => removeTab(group, paneId));
  if (tree.focusedPaneId !== paneId) return normalize(next, opts);
  const remaining = findNode(next, found.group.id);
  const focus =
    remaining && remaining.type === "tabs" && remaining.tabs.length > 0
      ? activePane(remaining)?.id
      : siblingPane(tree, found.group.id);
  const out: LayoutTree = { version: next.version, root: next.root };
  if (focus !== undefined) out.focusedPaneId = focus;
  return normalize(out, opts);
}

export function splitPane(
  tree: LayoutTree,
  paneId: string,
  direction: SplitDirection,
  newPane: PaneInit,
  position: Position = "after",
  opts: IdOptions = {},
): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  const gen = idGen(opts);
  const pane = makePane(newPane, gen);
  const group: TabGroup = { type: "tabs", id: gen(), tabs: [pane], activeTabId: pane.id };
  const next = insertBeside(tree, found.group.id, group, direction, position, gen);
  return normalize({ ...next, focusedPaneId: pane.id }, opts);
}

export type MoveTarget =
  /** Dock outside the entire remaining workspace. */
  | { workspace: true; edge: Edge }
  /** Move into a group as a tab. `index` is the position among the group's current tabs. */
  | { groupId: string; index?: number }
  /** Dock beside the group containing `paneId` as a new split. */
  | { paneId: string; edge: Edge }
  /** Dock beside the group as a new split. */
  | { groupId: string; edge: Edge };

export function movePane(
  tree: LayoutTree,
  paneId: string,
  target: MoveTarget,
  opts: IdOptions = {},
): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  const gen = idGen(opts);

  if ("workspace" in target) {
    if (allPanes(tree).length < 2) return tree;
    const remaining = normalize(removePaneRaw(tree, paneId), opts);
    const group: TabGroup = { type: "tabs", id: gen(), tabs: [found.pane], activeTabId: paneId };
    const before = target.edge === "left" || target.edge === "top";
    return normalize(
      {
        ...remaining,
        focusedPaneId: paneId,
        root: {
          type: "split",
          id: gen(),
          direction: target.edge === "left" || target.edge === "right" ? "horizontal" : "vertical",
          children: before ? [group, remaining.root] : [remaining.root, group],
          ratios: [0.5, 0.5],
        },
      },
      opts,
    );
  }

  if ("edge" in target) {
    const targetGroupId =
      "paneId" in target ? findPane(tree, target.paneId)?.group.id : target.groupId;
    const targetGroup = targetGroupId !== undefined ? findNode(tree, targetGroupId) : undefined;
    if (!targetGroup || targetGroup.type !== "tabs") return tree;
    if (targetGroup.id === found.group.id && found.group.tabs.length === 1) return tree;
    const removed = removeForMove(tree, paneId, targetGroup.id);
    const group: TabGroup = { type: "tabs", id: gen(), tabs: [found.pane], activeTabId: paneId };
    const direction: SplitDirection =
      target.edge === "left" || target.edge === "right" ? "horizontal" : "vertical";
    const position: Position = target.edge === "left" || target.edge === "top" ? "before" : "after";
    const next = insertBeside(removed, targetGroup.id, group, direction, position, gen);
    return normalize({ ...next, focusedPaneId: paneId }, opts);
  }

  const targetGroup = findNode(tree, target.groupId);
  if (!targetGroup || targetGroup.type !== "tabs") return tree;
  if (targetGroup.id === found.group.id) {
    const from = found.group.tabs.findIndex((tab) => tab.id === paneId);
    const requested = target.index ?? found.group.tabs.length;
    const to = requested > from ? requested - 1 : requested;
    return focusPane(reorderTab(tree, targetGroup.id, from, to, opts), paneId, opts);
  }
  const removed = removeForMove(tree, paneId, targetGroup.id);
  const next = updateGroup(removed, targetGroup.id, (group) => {
    const tabs = [...group.tabs];
    const at = Math.max(0, Math.min(target.index ?? tabs.length, tabs.length));
    tabs.splice(at, 0, found.pane);
    return { ...group, tabs, activeTabId: paneId };
  });
  return normalize({ ...next, focusedPaneId: paneId }, opts);
}

/** Moves the tab at `fromIndex` so that it ends up at `toIndex` (indices in the resulting list). */
export function reorderTab(
  tree: LayoutTree,
  groupId: string,
  fromIndex: number,
  toIndex: number,
  opts: IdOptions = {},
): LayoutTree {
  const group = findNode(tree, groupId);
  if (!group || group.type !== "tabs") return tree;
  const tab = group.tabs[fromIndex];
  if (!tab) return tree;
  const to = Math.max(0, Math.min(toIndex, group.tabs.length - 1));
  if (to === fromIndex) return tree;
  return normalize(
    updateGroup(tree, groupId, (g) => {
      const tabs = [...g.tabs];
      tabs.splice(fromIndex, 1);
      tabs.splice(to, 0, tab);
      return { ...g, tabs };
    }),
    opts,
  );
}

/** Makes the pane its group's active tab and focuses it. */
export function activateTab(tree: LayoutTree, paneId: string, opts: IdOptions = {}): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  return normalize(
    {
      ...updateGroup(tree, found.group.id, (g) => ({ ...g, activeTabId: paneId })),
      focusedPaneId: paneId,
    },
    opts,
  );
}

/** Focuses the pane (which also makes it its group's active tab). */
export function focusPane(tree: LayoutTree, paneId: string, opts: IdOptions = {}): LayoutTree {
  if (tree.focusedPaneId === paneId) return tree;
  return activateTab(tree, paneId, opts);
}

export type ResizeOptions = IdOptions & {
  /** Smallest ratio a child may be resized to (default 0.1). */
  minRatio?: number;
};

/**
 * Moves the gutter between children `gutterIndex` and `gutterIndex + 1` by `delta` pixels
 * within a split that is `containerSize` pixels along its axis.
 */
export function resizeSplit(
  tree: LayoutTree,
  splitId: string,
  gutterIndex: number,
  delta: number,
  containerSize: number,
  opts: ResizeOptions = {},
): LayoutTree {
  const split = findNode(tree, splitId);
  if (!split || split.type !== "split" || containerSize <= 0) return tree;
  const minRatio = opts.minRatio ?? 0.1;
  const ratios = normalizeRatios(split.ratios, split.children.length);
  const a = ratios[gutterIndex];
  const b = ratios[gutterIndex + 1];
  if (a === undefined || b === undefined) return tree;
  if (a + b < 2 * minRatio) return tree;
  const shift = Math.max(minRatio - a, Math.min(b - minRatio, delta / containerSize));
  const next = [...ratios];
  next[gutterIndex] = a + shift;
  next[gutterIndex + 1] = b - shift;
  return normalize(
    mapTree(tree, (node) => (node.id === splitId ? { ...node, ratios: next } : node)),
    opts,
  );
}

/** Gives the two children adjacent to a gutter equal shares of their combined ratio. */
export function equalizeGutter(
  tree: LayoutTree,
  splitId: string,
  gutterIndex: number,
  opts: IdOptions = {},
): LayoutTree {
  const split = findNode(tree, splitId);
  if (!split || split.type !== "split") return tree;
  const ratios = normalizeRatios(split.ratios, split.children.length);
  const a = ratios[gutterIndex];
  const b = ratios[gutterIndex + 1];
  if (a === undefined || b === undefined) return tree;
  const next = [...ratios];
  next[gutterIndex] = (a + b) / 2;
  next[gutterIndex + 1] = (a + b) / 2;
  return normalize(
    mapTree(tree, (node) => (node.id === splitId ? { ...node, ratios: next } : node)),
    opts,
  );
}

/**
 * Swap the pane at `paneId` for a new pane in the same tab position. Used for "navigation
 * replaces the current view" behaviour. Focus and activation follow the new pane when the old
 * one had them.
 */
export function replacePane(
  tree: LayoutTree,
  paneId: string,
  init: PaneInit,
  opts: IdOptions = {},
): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  const newPane = makePane(init, idGen(opts));
  const next = updateGroup(tree, found.group.id, (group) =>
    withActive(
      { ...group, tabs: group.tabs.map((tab) => (tab.id === paneId ? newPane : tab)) },
      group.activeTabId === paneId ? newPane.id : group.activeTabId,
    ),
  );
  return normalize(
    tree.focusedPaneId === paneId ? { ...next, focusedPaneId: newPane.id } : next,
    opts,
  );
}

/** Close every other tab in the pane's group. */
export function closeOtherTabs(tree: LayoutTree, paneId: string, opts: IdOptions = {}): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found) return tree;
  const next = updateGroup(tree, found.group.id, (group) =>
    withActive({ ...group, tabs: group.tabs.filter((tab) => tab.id === paneId) }, paneId),
  );
  return normalize({ ...next, focusedPaneId: paneId }, opts);
}

/** Close every tab in a group; the group itself collapses away unless it is the last one. */
export function closeAllTabs(tree: LayoutTree, groupId: string, opts: IdOptions = {}): LayoutTree {
  const group = findNode(tree, groupId);
  if (!group || group.type !== "tabs") return tree;
  let next = tree;
  for (const tab of group.tabs) next = closePane(next, tab.id, opts);
  return next;
}

export function updatePaneParams(
  tree: LayoutTree,
  paneId: string,
  params: Record<string, unknown>,
): LayoutTree {
  if (!findPane(tree, paneId)) return tree;
  return updatePane(tree, paneId, (pane) => ({ ...pane, params }));
}

export function setPaneTitle(
  tree: LayoutTree,
  paneId: string,
  title: string | undefined,
): LayoutTree {
  if (!findPane(tree, paneId)) return tree;
  return updatePane(tree, paneId, (pane) => {
    const { title: _old, ...rest } = pane;
    return title === undefined ? rest : { ...rest, title };
  });
}

/** Combines every tab into the focused group without closing panes or losing their params. */
export function combineGroups(tree: LayoutTree): LayoutTree {
  const group = defaultGroup(tree);
  if (!group || tree.root.type === "tabs") return tree;
  const byKey = new Map<string, Pane>();
  for (const pane of allPanes(tree)) {
    const key = JSON.stringify([
      pane.paneType,
      Object.entries(pane.params).sort(([a], [b]) => a.localeCompare(b)),
    ]);
    if (!byKey.has(key) || pane.id === tree.focusedPaneId) byKey.set(key, pane);
  }
  const tabs = [...byKey.values()];
  const activeTabId = tabs.find((pane) => pane.id === tree.focusedPaneId)?.id ?? tabs[0]?.id;
  return { ...tree, root: { ...group, tabs, ...(activeTabId ? { activeTabId } : {}) } };
}
